import { useTranslation } from 'react-i18next';
import { Link2, ShieldAlert, ShieldQuestion } from 'lucide-react';

import { CardIcon } from '@/components/cr/CardIcon';
import { cn } from '@/lib/cn';
import { ImportPill, SavePill } from './DeckActions';
import type { CounterDeckResponse, CRCDoctorCard, CRCDoctorDeck } from '@/types/qbot';

/** Doctor/counter card form → CardIcon form. Base is the default. */
function iconForm(form: CRCDoctorCard['form']): 'base' | 'evolution' | 'hero' {
  return form === 'Evo' ? 'evolution' : form === 'Hero' ? 'hero' : 'base';
}

/**
 * estWinRate/synergy scores arrive on two scales in this codebase: qCrC's
 * advisor payload uses a 0-1 fraction, the live fallback a 0-100 percentage.
 * Normalise to a percentage.
 */
function toPct(v: number): number {
  return v <= 1 ? v * 100 : v;
}

/** matchupBucket → tone. 'even' | 'unknown' | any custom string stays muted. */
function bucketTone(bucket: string): string {
  if (bucket === 'favorable') return 'text-emerald-400';
  if (bucket === 'unfavorable') return 'text-rose-300';
  return 'text-fg-muted';
}

/** The optimized deck report (Deck Doctor). Pure display — no data fetching. */
export function DoctorResult({
  deck,
  onSave,
  saving,
  saved,
  saveError,
  saveLabel,
  savedLabel,
}: {
  deck: CRCDoctorDeck;
  onSave: () => void;
  saving: boolean;
  saved: boolean;
  saveError: string | null;
  saveLabel: string;
  savedLabel: string;
}) {
  const { t } = useTranslation('cr');
  const hasUnderLeveled = deck.cards.some((c) => c.underLeveled);

  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-2 rounded-xl bg-bg-secondary px-3 py-2">
        <span className="text-lg font-bold tabular-nums text-brand">
          {t('panel.doctor.rating', { rating: deck.rating })}
        </span>
        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-fg">
          {deck.archetype}
        </span>
        <span className="shrink-0 text-[10px] tabular-nums text-fg-muted">
          {t('panel.doctor.avgElixir', { value: deck.avgElixirCost.toFixed(1) })}
        </span>
      </div>

      <div className="grid grid-cols-8 gap-1">
        {deck.cards.map((c) => (
          <CardIcon
            key={`${c.id}-${c.form}`}
            id={c.id}
            name={c.name}
            form={iconForm(c.form)}
            level={c.displayLevel}
            className={c.underLeveled ? 'ring-2 ring-amber-400/70' : undefined}
          />
        ))}
      </div>
      {hasUnderLeveled && (
        <p className="text-[10px] leading-snug text-amber-400/90">
          {t('panel.doctor.underLeveledNote')}
        </p>
      )}

      <div className="flex flex-col gap-1">
        <div className="grid grid-cols-2 gap-1.5">
          <SavePill
            saved={saved}
            saving={saving}
            onSave={onSave}
            label={saveLabel}
            savedLabel={savedLabel}
          />
        </div>
        {saveError && <p className="text-[10px] text-rose-300">{saveError}</p>}
      </div>

      {deck.topSynergies.length > 0 && (
        <section className="space-y-1.5">
          <h3 className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
            <Link2 className="h-3.5 w-3.5 text-brand" />
            {t('panel.doctor.synergies')}
          </h3>
          <ul className="space-y-1">
            {deck.topSynergies.map((s) => (
              <li
                key={`${s.a}-${s.b}`}
                className="flex items-center gap-2 rounded-lg bg-bg-secondary px-2.5 py-1.5"
              >
                <span className="min-w-0 flex-1 truncate text-xs text-fg">
                  {t('panel.doctor.pair', { a: s.nameA, b: s.nameB })}
                </span>
                <span className="shrink-0 text-[10px] font-semibold tabular-nums text-emerald-400">
                  {Math.round(toPct(s.score))}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {deck.weaknesses.length > 0 && (
        <section className="space-y-1.5">
          <h3 className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-fg-muted">
            <ShieldAlert className="h-3.5 w-3.5 text-rose-300" />
            {t('panel.doctor.weaknesses')}
          </h3>
          <ul className="space-y-1">
            {deck.weaknesses.map((w) => (
              <li
                key={`${w.cardId}-${w.kind}`}
                className="flex items-center gap-2 rounded-lg bg-bg-secondary px-2.5 py-1.5"
              >
                <span className="min-w-0 flex-1 truncate text-xs text-fg">{w.name}</span>
                <span className="shrink-0 text-[10px] text-fg-muted">{w.kind}</span>
                <span className="shrink-0 text-[10px] font-semibold tabular-nums text-rose-300">
                  {t('panel.doctor.wr', { value: Math.round(toPct(w.estWinRate)) })}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** The counter-deck result: honest verdict headline + the recommended deck. */
export function CounterResult({ result }: { result: CounterDeckResponse }) {
  const { t } = useTranslation('cr');
  const deck = result.counterDeck;
  const bucket = result.matchupBucket;
  const knownBucket =
    bucket === 'favorable' || bucket === 'even' || bucket === 'unfavorable' || bucket === 'unknown';
  const verdictLabel = knownBucket ? t(`panel.counter.verdict.${bucket}`) : bucket;

  const est = result.estimatedWinRate;
  const estPct = typeof est === 'number' ? Math.round(est <= 1 ? est * 100 : est) : null;

  return (
    <div className="space-y-2">
      <div>
        <div className="flex items-baseline gap-2">
          <span className={cn('text-base font-bold uppercase tracking-wide', bucketTone(bucket))}>
            {verdictLabel}
          </span>
          {estPct !== null ? (
            <span className="text-[11px] tabular-nums text-fg-muted">
              {t('panel.counter.estWinRate', { value: estPct })}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] text-fg-muted">
              <ShieldQuestion className="h-3 w-3" />
              {t('panel.counter.estWinRateNa')}
            </span>
          )}
        </div>
        <p className="text-[11px] leading-snug text-fg-muted">
          {t('panel.counter.counterHeading')}
        </p>
      </div>

      <div className="grid grid-cols-4 gap-1.5">
        {deck.cards.map((card, i) => (
          <CardIcon
            key={`${card.id}-${i}`}
            id={card.id}
            name={card.name}
            level={card.displayLevel}
            form={iconForm(card.form)}
            className="w-full"
          />
        ))}
      </div>

      <div className="flex items-baseline gap-2 text-xs">
        {deck.archetype ? (
          <span className="min-w-0 truncate font-bold text-fg">{deck.archetype}</span>
        ) : null}
        <span className="shrink-0 tabular-nums text-brand">
          {t('panel.counter.rating', { value: deck.rating.toFixed(1) })}
        </span>
        <span className="ml-auto shrink-0 tabular-nums text-fg-muted">
          ⚡ {deck.avgElixirCost.toFixed(1)}
        </span>
      </div>

      {/* Hand the suggested counter straight to the game. `form` is the advisor's
          way of saying "evolved", so map it back to the evolution slot the deep
          link expects — otherwise the import silently drops the evolutions. */}
      <ImportPill
        cards={deck.cards.map((c) => ({ id: c.id, evolutionLevel: c.form === 'Evo' ? 1 : 0 }))}
        label={t('panel.action.import')}
      />

      {deck.topSynergies.length > 0 && (
        <div>
          <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-fg-muted">
            {t('panel.counter.synergies')}
          </p>
          <ul className="space-y-1">
            {deck.topSynergies.slice(0, 3).map((s, i) => (
              <li
                key={`${s.a}-${s.b}-${i}`}
                className="flex items-center gap-1.5 rounded-lg bg-bg-secondary px-2 py-1 text-[11px] text-fg"
              >
                <span className="min-w-0 truncate">{s.nameA}</span>
                <span className="shrink-0 text-fg-muted">+</span>
                <span className="min-w-0 truncate">{s.nameB}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
